import _ from 'lodash';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { createOrUpdateCatalogItem, bulkUpdateCatalogItems } from 'api/CatalogAPI';
import { getCatalogPending, getCatalogPendingDelete } from 'modules/catalogTable/selectors';
import { addToRootSaga } from 'store';
import {
  updateCatalogFieldTypesRequest,
  checkIfFieldTypesUpdated,
} from 'modules/catalogFieldTypes';
import { getErrorResponse } from 'utils/xhr';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import * as actions from './actions';
import * as actionTypes from './actionTypes';

export function* updateCatalogItemRequest({ tableName, tableKey, tableValue }) {
  try {
    const jsonValue = JSON.parse(tableValue);
    const { data } = yield call(createOrUpdateCatalogItem, tableName, tableKey, jsonValue);
    let { pending, pendingDelete } = yield all({
      pending: select(getCatalogPending),
      pendingDelete: select(getCatalogPendingDelete),
    });
    const currentPending = _.get(pending, tableName) || [];
    pending = {
      ...pending,
      [tableName]: _.unionBy(currentPending, [{ name: tableKey }], 'name'),
    };
    pendingDelete = {
      ...pendingDelete,
      [tableName]: _.omit(_.get(pendingDelete, tableName, {}), tableKey),
    };
    yield put(actions.updateCatalogItemResponse(pending, pendingDelete));
    yield put(addPositiveToast(data));
    return true;
  } catch (error) {
    const err = getErrorResponse(error);
    const errMsg = `Cannot create data. ${err}`;
    yield put(actions.updateCatalogItemError(err));
    yield put(addNegativeToast(errMsg));
    return false;
  }
}

export function* updateFieldsAndCatalogItemRequest({ tableName, tableKey, tableValue, fields }) {
  try {
    const step1 = yield* updateCatalogFieldTypesRequest({ tableName, fieldTypes: fields });
    if (step1) {
      const step2 = yield* checkIfFieldTypesUpdated({ tableName, fields });
      if (step2) {
        yield* updateCatalogItemRequest({ tableName, tableKey, tableValue });
      }
    }
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.updateFieldsAndCatalogItemError());
    yield put(addNegativeToast(err));
  }
}

export function* updateBulkCatalogItemsRequest({ tableName, file }) {
  try {
    const { data } = yield call(bulkUpdateCatalogItems, tableName, file);
    yield put(actions.updateBulkCatalogItemsResponse(data));
    yield put(addPositiveToast(data));
  } catch (error) {
    const err = getErrorResponse(error);
    const errMsg = `Cannot create data. ${err}`;
    // TODO maybe yield all here
    yield put(actions.updateBulkCatalogItemsError(err));
    yield put(addNegativeToast(errMsg));
  }
}

export function* updateFieldsAndBulkCatalogItemsRequest({ tableName, file, fields }) {
  try {
    const step1 = yield* updateCatalogFieldTypesRequest({ tableName, fieldTypes: fields });
    if (step1) {
      const step2 = yield* checkIfFieldTypesUpdated({ tableName, fields });
      if (step2) {
        yield* updateBulkCatalogItemsRequest({ tableName, file });
      }
    }
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.updateFieldsAndBulkCatalogItemsError());
    yield put(addNegativeToast(err));
  }
}

addToRootSaga([
  takeLatest(actionTypes.UPDATE_CATALOG_ITEM_REQUEST, updateCatalogItemRequest),
  takeLatest(actionTypes.UPDATE_FIELDS_AND_CATALOG_ITEM_REQUEST, updateFieldsAndCatalogItemRequest),
  takeLatest(actionTypes.UPDATE_BULK_CATALOG_ITEMS_REQUEST, updateBulkCatalogItemsRequest),
  takeLatest(
    actionTypes.UPDATE_FIELDS_AND_BULK_CATALOG_ITEMS_REQUEST,
    updateFieldsAndBulkCatalogItemsRequest,
  ),
]);

export default updateCatalogItemRequest;
